"use client";

import { FormEvent, useState } from "react";
import { CompanionAvatar } from "./CompanionAvatar";
import type { Companion } from "./data";
import { starters } from "./data";

type ChatMessage = { id: number; text: string; from: "user" | "system" };

export function ChatPanel({ companion }: { companion: Companion }) {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  async function sendMessage(event?: FormEvent) {
    event?.preventDefault();
    const text = input.trim();
    if (!text || isSending) return;
    setInput("");
    setMessages((current) => [...current, { id: Date.now(), text, from: "user" }]);
    setIsSending(true);
    try {
      const response = await fetch("/api/chat", { method: "POST" });
      const responseText = await response.text();
      let result: { message?: string } = {};
      try {
        result = JSON.parse(responseText) as { message?: string };
      } catch {
        result = {};
      }
      setMessages((current) => [
        ...current,
        {
          id: Date.now() + 1,
          text: result.message ?? "Не удалось получить ответ. Попробуйте ещё раз чуть позже.",
          from: "system",
        },
      ]);
    } catch {
      setMessages((current) => [
        ...current,
        {
          id: Date.now() + 1,
          text: "Не удалось связаться с сервисом. Проверьте соединение и попробуйте ещё раз.",
          from: "system",
        },
      ]);
    } finally {
      setIsSending(false);
    }
  }
  return (
    <section className="chat-panel" data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:52:5"}>
      <header className="chat-header" data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:53:7"}>
        <div className="chat-identity" data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:54:9"}>
          <CompanionAvatar companion={companion} small data-amazi-jsx-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:55:11"} />
          <div data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:56:11"}>
            <h1 data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:57:13"}>{companion.name}</h1>
            <span data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:58:13"}>
              <i data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:59:15"} /> {companion.role}
            </span>
          </div>
        </div>
        <button className="more-button" aria-label="Дополнительные действия" data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:63:9"}>
          •••
        </button>
      </header>
      <div className="chat-body" data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:67:7"}>
        <div className="welcome-message" data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:68:9"}>
          <CompanionAvatar companion={companion} data-amazi-jsx-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:69:11"} />
          <div data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:70:11"}>
            <span className="eyebrow" data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:71:13"}>Начало разговора</span>
            <p data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:72:13"}>{companion.greeting}</p>
            <div className="tag-row" data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:73:13"}>
              {companion.tags.map((tag) => (
                <span key={tag} data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:75:17"}>#{tag}</span>
              ))}
            </div>
          </div>
        </div>
        {messages.map((message) => (
          <div
            className={`message ${message.from === "user" ? "message--user" : "message--system"}`}
            key={message.id}
           data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:81:11"}>
            {message.text}
          </div>
        ))}
        {isSending && (
          <div className="typing" data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:89:11"}>
            <span data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:90:13"} />
            <span data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:91:13"} />
            <span data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:92:13"} /> {companion.name} слушает…
          </div>
        )}
        {messages.length === 0 && (
          <div className="starter-block" data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:96:11"}>
            <span className="starter-label" data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:97:13"}>Можно начать с этого</span>
            <div className="starter-list" data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:98:13"}>
              {starters.map((starter) => (
                <button key={starter} onClick={() => setInput(starter)} data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:100:17"}>
                  {starter}
                  <span data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:102:19"}>↗</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <form className="message-form" onSubmit={sendMessage} data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:109:7"}>
        <input
          aria-label="Сообщение"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder={`Напиши ${companion.name}…`} data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:110:9"}
        />
        <button aria-label="Отправить сообщение" disabled={!input.trim() || isSending} data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:116:9"}>
          ↑
        </button>
      </form>
      <p className="chat-disclaimer" data-amazi-id={"луна-ии-компаньоны/src/features/companion/ChatPanel.tsx:120:7"}>
        ИИ-компаньонки — вымышленные персонажи. Не заменяют профессиональную помощь.
      </p>
    </section>
  );
}
